/**
 * Workspace Permissions
 * 工作区相关权限
 */
export enum WorkspacePermission {
  /** View workspace details - 查看工作区详情 */
  VIEW = 'workspace:view',
  /** Update workspace settings - 更新工作区设置 */
  UPDATE = 'workspace:update',
  /** Delete workspace - 删除工作区 */
  DELETE = 'workspace:delete',
  /** Transfer workspace ownership - 转移工作区所有权 */
  TRANSFER_OWNERSHIP = 'workspace:transfer_ownership',
}

/**
 * Member Permissions
 * 成员相关权限
 */
export enum MemberPermission {
  /** View workspace members - 查看工作区成员 */
  VIEW = 'member:view',
  /** Invite new members - 邀请新成员 */
  INVITE = 'member:invite',
  /** Remove members - 移除成员 */
  REMOVE = 'member:remove',
  /** Change member roles - 修改成员角色 */
  CHANGE_ROLE = 'member:change_role',
}

/**
 * Integration Permissions
 * 集成相关权限
 */
export enum IntegrationPermission {
  /** View integrations - 查看集成 */
  VIEW = 'integration:view',
  /** Connect new integrations - 连接新集成 */
  CONNECT = 'integration:connect',
  /** Disconnect integrations - 断开集成 */
  DISCONNECT = 'integration:disconnect',
}

/**
 * Dashboard Permissions
 * 仪表板相关权限
 */
export enum DashboardPermission {
  /** View dashboards - 查看仪表板 */
  VIEW = 'dashboard:view',
  /** Create new dashboards - 创建新仪表板 */
  CREATE = 'dashboard:create',
  /** Edit dashboards - 编辑仪表板 */
  EDIT = 'dashboard:edit',
  /** Delete dashboards - 删除仪表板 */
  DELETE = 'dashboard:delete',
  /** Manage dashboards (full control) - 管理仪表板（完全控制） */
  MANAGE = 'dashboard:manage',
}

/**
 * Pulse Permissions
 * Pulse 相关权限
 */
export enum PulsePermission {
  /** View pulse - 查看 Pulse */
  VIEW = 'pulse:view',
  /** Curate pulse - 策展 Pulse */
  CURATE = 'pulse:curate',
  /** Receive pulse notifications - 接收 Pulse 通知 */
  RECEIVE = 'pulse:receive',
}

/**
 * Subscription Permissions
 * 订阅相关权限
 */
export enum SubscriptionPermission {
  /** View subscription details - 查看订阅详情 */
  VIEW = 'subscription:view',
  /** Manage subscription - 管理订阅 */
  MANAGE = 'subscription:manage',
}

export enum UsagePermission {
  /** View usage details - 查看用量详情 */
  VIEW = 'usage:view',
}

export enum OutcomeSummaryPermission {
  /** View Outcome Summary - 查看成果总览 */
  VIEW = 'outcome_summary:view',
  /** Manage Outcome Summary - 管理成果总览 */
  MANAGE = 'outcome_summary:manage',
}

export enum AuditLogPermission {
  /** View cloud audit log, source conversations, and content details - 查看云端审计日志、原始对话和内容详情 */
  VIEW = 'audit_log:view',
}

export const AUDIT_LOG_SYSTEM_PERMISSION = 'agent_run_ledger';

/**
 * Workspace Skill Permissions
 * 工作区技能相关权限
 */
export enum WorkspaceSkillPermission {
  /** Manage workspace-created skills - 管理工作区沉淀的技能 */
  MANAGE = 'workspace_skill:manage',
}

/**
 * Brand Permissions
 * 品牌相关权限
 */
export enum BrandPermission {
  /** Assign and remove workspace user access to Brands - 管理品牌访问权限 */
  MANAGE_ACCESS = 'brand:manage_access',
}

/**
 * Unified Permission Enum
 * 统一权限枚举，包含所有权限类别
 */
export enum Permission {
  // Workspace
  WORKSPACE_VIEW = WorkspacePermission.VIEW,
  WORKSPACE_UPDATE = WorkspacePermission.UPDATE,
  WORKSPACE_DELETE = WorkspacePermission.DELETE,
  WORKSPACE_TRANSFER_OWNERSHIP = WorkspacePermission.TRANSFER_OWNERSHIP,

  // Member
  MEMBER_VIEW = MemberPermission.VIEW,
  MEMBER_INVITE = MemberPermission.INVITE,
  MEMBER_REMOVE = MemberPermission.REMOVE,
  MEMBER_CHANGE_ROLE = MemberPermission.CHANGE_ROLE,

  // Integration
  INTEGRATION_VIEW = IntegrationPermission.VIEW,
  INTEGRATION_CONNECT = IntegrationPermission.CONNECT,
  INTEGRATION_DISCONNECT = IntegrationPermission.DISCONNECT,

  // Dashboard
  DASHBOARD_VIEW = DashboardPermission.VIEW,
  DASHBOARD_MANAGE = DashboardPermission.MANAGE,

  // Pulse
  PULSE_VIEW = PulsePermission.VIEW,
  PULSE_CURATE = PulsePermission.CURATE,

  // Subscription
  SUBSCRIPTION_VIEW = SubscriptionPermission.VIEW,
  SUBSCRIPTION_MANAGE = SubscriptionPermission.MANAGE,

  // Usage
  USAGE_VIEW = UsagePermission.VIEW,

  // Outcome Summary
  OUTCOME_SUMMARY_VIEW = OutcomeSummaryPermission.VIEW,
  OUTCOME_SUMMARY_MANAGE = OutcomeSummaryPermission.MANAGE,

  // Workspace Skill
  WORKSPACE_SKILL_MANAGE = WorkspaceSkillPermission.MANAGE,

  // Brand
  BRAND_MANAGE_ACCESS = BrandPermission.MANAGE_ACCESS,

  // Audit Log
  AUDIT_LOG_VIEW = AuditLogPermission.VIEW,
}

/**
 * Permission categories
 * 权限类别
 */
export const PERMISSION_CATEGORIES = {
    WORKSPACE: 'workspace',
    MEMBER: 'member',
    INTEGRATION: 'integration',
    DASHBOARD: 'dashboard',
    PULSE: 'pulse',
    SUBSCRIPTION: 'subscription',
    USAGE: 'usage',
    OUTCOME_SUMMARY: 'outcome_summary',
    WORKSPACE_SKILL: 'workspace_skill',
    BRAND: 'brand',
    AUDIT_LOG: 'audit_log',
} as const;

export type PermissionCategory = typeof PERMISSION_CATEGORIES[keyof typeof PERMISSION_CATEGORIES];
